import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faSquareParking,
    faUserCheck,
    faKey,
    faReceipt,
    faCalendarAlt,
    faDownload,
    faSearch,
    faFilter,
    faRotateRight,
    faSpinner
} from "@fortawesome/free-solid-svg-icons";

import Breadcrumbs from "../../../../common/BreadCrum/BreadCrum";
import PageHeadingSection from "../../../../common/pageheading/PageHeadingSection";
import SectionTab from "../../../../common/sectiontab/SectionTab";
import CommonTable from "../../../../common/table/ComonTable";
import apiServices from "../../../../../services/apiService";
import { exportToExcel } from "../../../../../utils/excelUtils";
import { getStatusBadgeStyle } from "../../../../../utils/statusUtils";
import { primaryColor } from "../../../../../utils/comon";

const inRange = (start, end, from, to) => {
    if (!from && !to) return true;
    const startDate = start ? new Date(start) : null;
    const endDate = end ? new Date(end) : startDate;
    if (from && endDate && endDate < new Date(from)) return false;
    if (to && startDate && startDate > new Date(`${to}T23:59:59`)) return false;
    return Boolean(startDate || endDate);
};

const rowsOf = (res) => res?.data?.Data || res?.data?.data || [];

const formatDate = (dateStr) => {
    if (!dateStr || String(dateStr).includes("0001-01-01")) return "—";
    try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return String(dateStr);
        return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
        return String(dateStr);
    }
};

const formatDateTime = (dateStr) => {
    if (!dateStr || String(dateStr).includes("0001-01-01")) return "—";
    try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return String(dateStr);
        return d.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
    } catch {
        return String(dateStr);
    }
};

const ParkingReports = () => {
    const { user } = useSelector((state) => state.auth);
    const roleId = Number(user?.roleId || 3);
    const isUnitAdmin = roleId === 4 || roleId === 6;
    const siteId = user?.siteId;
    const unitId = user?.unitId || user?.branchorunitID;

    // Filters
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [activeTab, setActiveTab] = useState("slots");

    // Loading & Data States
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState(null);
    const [slots, setSlots] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [rentals, setRentals] = useState([]);
    const [bookings, setBookings] = useState([]);

    // Pagination per tab
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Initial Date Preset: Default to Current Month
    useEffect(() => {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split("T")[0];
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split("T")[0];
        setFrom(firstDay);
        setTo(lastDay);
    }, []);

    // Load Data
    const loadReports = useCallback(async (fromDate = from, toDate = to) => {
        if (isUnitAdmin && !unitId) return;
        if (!isUnitAdmin && !siteId) return;

        setLoading(true);
        try {
            const query = isUnitAdmin ? `unitId=${unitId}` : `siteId=${siteId}`;
            const dateQuery = `${query}${fromDate ? `&startDate=${fromDate}` : ""}${toDate ? `&endDate=${toDate}` : ""}`;

            const [dash, slotRes, assignRes, rentalRes, bookingRes, typeRes] = await Promise.all([
                apiServices({ method: "GET", url: `/ParkingDashboard?${dateQuery}` }).catch(() => null),
                apiServices({ method: "GET", url: isUnitAdmin ? `/ParkingSlotMaster/unit/${unitId}` : `/ParkingSlotMaster/site/${siteId}` }).catch(() => null),
                apiServices({ method: "GET", url: `/ParkingAssignment?${query}` }).catch(() => null),
                apiServices({ method: "GET", url: isUnitAdmin ? `/RentalParking/unit/${unitId}` : `/RentalParking/site/${siteId}` }).catch(() => null),
                apiServices({ method: "GET", url: `/VisitorParkingBooking?${query}` }).catch(() => null),
                apiServices({ method: "GET", url: `/AllocationTypeMaster/site/${siteId}` }).catch(() => null)
            ]);

            setSummary(dash?.data || null);

            const slotRows = rowsOf(slotRes);
            setSlots(slotRows.filter((row) => !row.CreatedOn || inRange(row.CreatedOn, row.CreatedOn, fromDate, toDate)));
            setAssignments(rowsOf(assignRes).filter((row) => inRange(row.StartDate || row.startDate, row.EndDate || row.endDate, fromDate, toDate)));
            setRentals(rowsOf(rentalRes).filter((row) => inRange(row.StartDate || row.startDate, row.EndDate || row.endDate, fromDate, toDate)));

            const types = rowsOf(typeRes);
            const payType = types.find((type) => {
                const clean = String(type.AllocationTypeName || type.allocationTypeName || "").trim().toLowerCase().replace(/[^a-z]/g, "");
                return clean.includes("payandpark") || clean.includes("paypark");
            });
            const payTypeId = payType ? Number(payType.Id || payType.id) : null;
            const paySlotIds = new Set(
                slotRows
                    .filter((slot) => Number(slot.AllocationTypeId || slot.allocationTypeId) === payTypeId)
                    .map((slot) => Number(slot.Id || slot.id))
            );

            setBookings(
                rowsOf(bookingRes).filter((row) => {
                    const matchSlot = paySlotIds.size === 0 || paySlotIds.has(Number(row.SlotId || row.slotId));
                    return matchSlot && inRange(row.StartDate || row.startDate || row.CreatedOn, row.EndDate || row.endDate || row.CreatedOn, fromDate, toDate);
                })
            );
        } catch (error) {
            console.error("Error loading parking reports:", error);
            toast.error("Failed to load parking reports data.");
        } finally {
            setLoading(false);
        }
    }, [isUnitAdmin, unitId, siteId, from, to]);

    // Auto-fetch on mount when from/to or user are ready
    useEffect(() => {
        if (from && to && (siteId || unitId)) {
            loadReports(from, to);
        }
    }, [siteId, unitId, from, to, loadReports]);

    // Handle Quick Date Presets
    const handlePresetChange = (preset) => {
        const today = new Date();
        let newFrom = "";
        let newTo = "";

        if (preset === "thisMonth") {
            newFrom = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split("T")[0];
            newTo = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split("T")[0];
        } else if (preset === "last30Days") {
            const d = new Date();
            d.setDate(d.getDate() - 30);
            newFrom = d.toISOString().split("T")[0];
            newTo = today.toISOString().split("T")[0];
        } else if (preset === "allTime") {
            newFrom = "";
            newTo = "";
        }

        setFrom(newFrom);
        setTo(newTo);
        setPageNumber(1);
        loadReports(newFrom, newTo);
    };

    const handleReset = () => {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split("T")[0];
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split("T")[0];
        setFrom(firstDay);
        setTo(lastDay);
        setSearchQuery("");
        setPageNumber(1);
        loadReports(firstDay, lastDay);
    };

    // Filtered data per tab based on search query
    const filteredSlots = useMemo(() => {
        const q = searchQuery.toLowerCase().trim();
        if (!q) return slots;
        return slots.filter((item) =>
            String(item.SlotNumber || item.slotNumber || "").toLowerCase().includes(q) ||
            String(item.AllocationTypeName || item.allocationTypeName || "").toLowerCase().includes(q) ||
            String(item.AllocatedUnitName || item.allocatedUnitName || "").toLowerCase().includes(q) ||
            String(item.Status || item.status || "").toLowerCase().includes(q)
        );
    }, [slots, searchQuery]);

    const filteredAssignments = useMemo(() => {
        const q = searchQuery.toLowerCase().trim();
        if (!q) return assignments;
        return assignments.filter((item) =>
            String(item.SlotNumber || item.slotNumber || "").toLowerCase().includes(q) ||
            String(item.AssignedUserName || item.assignedUserName || "").toLowerCase().includes(q) ||
            String(item.VehicleNumber || item.vehicleNumber || "").toLowerCase().includes(q) ||
            String(item.MobileNumber || item.mobileNumber || "").toLowerCase().includes(q) ||
            String(item.Status || item.status || "").toLowerCase().includes(q)
        );
    }, [assignments, searchQuery]);

    const filteredRentals = useMemo(() => {
        const q = searchQuery.toLowerCase().trim();
        if (!q) return rentals;
        return rentals.filter((item) =>
            String(item.SlotNumber || item.slotNumber || item.slot || "").toLowerCase().includes(q) ||
            String(item.OwnerUnitName || item.ownerUnitName || item.rentedFrom || "").toLowerCase().includes(q) ||
            String(item.RenterUnitName || item.renterUnitName || item.rentedTo || "").toLowerCase().includes(q) ||
            String(item.Status || item.status || "").toLowerCase().includes(q)
        );
    }, [rentals, searchQuery]);

    const filteredBookings = useMemo(() => {
        const q = searchQuery.toLowerCase().trim();
        if (!q) return bookings;
        return bookings.filter((item) =>
            String(item.VisitorName || item.visitorName || "").toLowerCase().includes(q) ||
            String(item.VisitorPhone || item.visitorPhone || "").toLowerCase().includes(q) ||
            String(item.VehicleNumber || item.vehicleNumber || "").toLowerCase().includes(q) ||
            String(item.SlotNumber || item.slotNumber || "").toLowerCase().includes(q) ||
            String(item.PaymentStatus || item.paymentStatus || "").toLowerCase().includes(q) ||
            String(item.Status || item.status || "").toLowerCase().includes(q)
        );
    }, [bookings, searchQuery]);

    // Export to Excel
    const handleExport = () => {
        try {
            if (activeTab === "slots") {
                if (filteredSlots.length === 0) {
                    toast.info("No slot records to export.");
                    return;
                }
                const headers = [
                    { label: "Slot Number", key: "slotNumber" },
                    { label: "Allocation Type", key: "allocationType" },
                    { label: "Floor", key: "floor" },
                    { label: "Wing / Zone", key: "wing" },
                    { label: "Allocated Unit", key: "allocatedUnit" },
                    { label: "Status", key: "status" }
                ];
                const exportData = filteredSlots.map((s) => ({
                    slotNumber: s.SlotNumber || s.slotNumber || "—",
                    allocationType: s.AllocationTypeName || s.allocationTypeName || "—",
                    floor: s.FloorName || s.floorName || "—",
                    wing: s.WingName || s.wingName || "—",
                    allocatedUnit: s.AllocatedUnitName || s.allocatedUnitName || "—",
                    status: s.Status || s.status || "—"
                }));
                exportToExcel(exportData, headers, "Parking_Slots_Inventory");
                toast.success("Slot directory exported successfully!");
            } else if (activeTab === "assignments") {
                if (filteredAssignments.length === 0) {
                    toast.info("No assignment records to export.");
                    return;
                }
                const headers = [
                    { label: "Slot Number", key: "slotNumber" },
                    { label: "Assigned User", key: "userName" },
                    { label: "Vehicle Number", key: "vehicleNumber" },
                    { label: "Vehicle Type", key: "vehicleType" },
                    { label: "Mobile Number", key: "mobileNumber" },
                    { label: "Start Date", key: "startDate" },
                    { label: "End Date", key: "endDate" },
                    { label: "Status", key: "status" }
                ];
                const exportData = filteredAssignments.map((a) => ({
                    slotNumber: a.SlotNumber || a.slotNumber || "—",
                    userName: a.AssignedUserName || a.assignedUserName || "—",
                    vehicleNumber: a.VehicleNumber || a.vehicleNumber || "—",
                    vehicleType: a.VehicleTypeName || a.vehicleTypeName || "—",
                    mobileNumber: a.MobileNumber || a.mobileNumber || "—",
                    startDate: formatDate(a.StartDate || a.startDate),
                    endDate: formatDate(a.EndDate || a.endDate),
                    status: a.Status || a.status || "—"
                }));
                exportToExcel(exportData, headers, "Parking_Assignments_Report");
                toast.success("Assignments exported successfully!");
            } else if (activeTab === "rentals") {
                if (filteredRentals.length === 0) {
                    toast.info("No rental records to export.");
                    return;
                }
                const headers = [
                    { label: "Rental ID", key: "id" },
                    { label: "Slot Number", key: "slotNumber" },
                    { label: "Rented From (Owner)", key: "rentedFrom" },
                    { label: "Rented To (Renter)", key: "rentedTo" },
                    { label: "Start Date", key: "startDate" },
                    { label: "End Date", key: "endDate" },
                    { label: "Monthly Rate", key: "rate" },
                    { label: "Status", key: "status" }
                ];
                const exportData = filteredRentals.map((r) => {
                    const rateVal = r.MonthlyRate || r.monthlyRate || r.rentAmount;
                    return {
                        id: r.Id || r.id || "—",
                        slotNumber: r.SlotNumber || r.slotNumber || r.slot || "—",
                        rentedFrom: r.OwnerUnitName || r.ownerUnitName || r.rentedFrom || "—",
                        rentedTo: r.RenterUnitName || r.renterUnitName || r.rentedTo || "—",
                        startDate: formatDate(r.StartDate || r.startDate || r.rentalPeriodStart),
                        endDate: formatDate(r.EndDate || r.endDate || r.rentalPeriodEnd),
                        rate: rateVal ? `₹${rateVal}` : "—",
                        status: r.Status || r.status || "—"
                    };
                });
                exportToExcel(exportData, headers, "Parking_Rentals_Report");
                toast.success("Rental leases exported successfully!");
            } else if (activeTab === "bookings") {
                if (filteredBookings.length === 0) {
                    toast.info("No visitor parking records to export.");
                    return;
                }
                const headers = [
                    { label: "Visitor Name", key: "visitorName" },
                    { label: "Phone", key: "phone" },
                    { label: "Slot Number", key: "slotNumber" },
                    { label: "Vehicle Number", key: "vehicleNumber" },
                    { label: "Start Time", key: "startTime" },
                    { label: "End Time", key: "endTime" },
                    { label: "Amount", key: "amount" },
                    { label: "Payment Status", key: "paymentStatus" },
                    { label: "Status", key: "status" }
                ];
                const exportData = filteredBookings.map((b) => ({
                    visitorName: b.VisitorName || b.visitorName || "—",
                    phone: b.VisitorPhone || b.visitorPhone || "—",
                    slotNumber: b.SlotNumber || b.slotNumber || "—",
                    vehicleNumber: b.VehicleNumber || b.vehicleNumber || "—",
                    startTime: formatDateTime(b.StartDate || b.startDate),
                    endTime: formatDateTime(b.EndDate || b.endDate),
                    amount: b.TotalAmount ? `₹${b.TotalAmount}` : "—",
                    paymentStatus: b.PaymentStatus || b.paymentStatus || "—",
                    status: b.Status || b.status || "—"
                }));
                exportToExcel(exportData, headers, "Parking_Visitor_PayAndPark_Report");
                toast.success("Visitor pay & park report exported successfully!");
            }
        } catch (e) {
            console.error("Export failed", e);
            toast.error("Failed to export report.");
        }
    };

    // Table Column Definitions
    const slotColumns = [
        { label: "SLOT NUMBER", key: "slotNumber" },
        { label: "PARKING TYPE", key: "allocationType" },
        { label: "VEHICLE TYPE", key: "vehicleType" },
        { label: "FLOOR / WING", key: "location" },
        { label: "ALLOCATED UNIT", key: "allocatedUnit" },
        { label: "STATUS", key: "status" }
    ];

    const assignmentColumns = [
        { label: "SLOT NUMBER", key: "slotNumber" },
        { label: "ASSIGNED USER", key: "assignedUser" },
        { label: "VEHICLE NO", key: "vehicleNumber" },
        { label: "VEHICLE TYPE", key: "vehicleType" },
        { label: "MOBILE", key: "mobileNumber" },
        { label: "START DATE", key: "startDate" },
        { label: "END DATE", key: "endDate" },
        { label: "STATUS", key: "status" }
    ];

    const rentalColumns = [
        { label: "RENTAL ID", key: "rentalId" },
        { label: "SLOT NUMBER", key: "slotNumber" },
        { label: "RENTED FROM (OWNER)", key: "rentedFrom" },
        { label: "RENTED TO (RENTER)", key: "rentedTo" },
        { label: "RENTAL PERIOD", key: "rentalPeriod" },
        { label: "MONTHLY RATE", key: "monthlyRate" },
        { label: "STATUS", key: "status" }
    ];

    const bookingColumns = [
        { label: "VISITOR", key: "visitorName" },
        { label: "PHONE", key: "visitorPhone" },
        { label: "SLOT NUMBER", key: "slotNumber" },
        { label: "VEHICLE NO", key: "vehicleNumber" },
        { label: "START TIME", key: "startTime" },
        { label: "END TIME", key: "endTime" },
        { label: "AMOUNT", key: "amount" },
        { label: "PAYMENT", key: "paymentStatus" },
        { label: "STATUS", key: "status" }
    ];

    // Cell Renderers
    const renderSlotCell = (key, row) => {
        switch (key) {
            case "slotNumber":
                return (
                    <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold text-xs">
                            <FontAwesomeIcon icon={faSquareParking} />
                        </div>
                        <span className="font-bold text-gray-800 dark:text-gray-100 text-xs">{row.SlotNumber || row.slotNumber || "—"}</span>
                    </div>
                );
            case "allocationType":
                return <span className="text-gray-700 dark:text-gray-300 font-medium text-xs">{row.AllocationTypeName || row.allocationTypeName || "Standard"}</span>;
            case "vehicleType":
                return <span className="text-gray-600 dark:text-gray-400 text-xs font-medium">{row.VehicleTypeName || row.vehicleTypeName || "4-Wheeler"}</span>;
            case "location": {
                const floor = row.FloorName || row.floorName || "";
                const wing = row.WingName || row.wingName || "";
                return <span className="text-gray-600 dark:text-gray-400 text-xs font-medium">{floor ? `${floor} ${wing ? `(${wing})` : ""}` : "—"}</span>;
            }
            case "allocatedUnit":
                return <span className="text-blue-600 dark:text-blue-400 font-semibold text-xs">{row.AllocatedUnitName || row.allocatedUnitName || "—"}</span>;
            case "status": {
                const st = row.Status || row.status || "Available";
                return <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${getStatusBadgeStyle(st)}`}>{st}</span>;
            }
            default:
                return "—";
        }
    };

    const renderAssignmentCell = (key, row) => {
        switch (key) {
            case "slotNumber":
                return <span className="font-bold text-gray-800 dark:text-gray-100 text-xs">{row.SlotNumber || row.slotNumber || "—"}</span>;
            case "assignedUser":
                return (
                    <div className="flex flex-col">
                        <span className="font-semibold text-gray-800 dark:text-gray-100 text-xs">{row.AssignedUserName || row.assignedUserName || "—"}</span>
                        {row.AllocatedUnitName && <span className="text-[10px] text-gray-400">Unit: {row.AllocatedUnitName}</span>}
                    </div>
                );
            case "vehicleNumber":
                return <span className="font-mono font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase">{row.VehicleNumber || row.vehicleNumber || "—"}</span>;
            case "vehicleType":
                return <span className="text-gray-600 dark:text-gray-400 text-xs">{row.VehicleTypeName || row.vehicleTypeName || "—"}</span>;
            case "mobileNumber":
                return <span className="text-gray-600 dark:text-gray-400 text-xs">{row.MobileNumber || row.mobileNumber || "—"}</span>;
            case "startDate":
                return <span className="text-gray-600 dark:text-gray-400 text-xs">{formatDate(row.StartDate || row.startDate)}</span>;
            case "endDate":
                return <span className="text-gray-600 dark:text-gray-400 text-xs">{formatDate(row.EndDate || row.endDate)}</span>;
            case "status": {
                const st = row.Status || row.status || "Active";
                return <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${getStatusBadgeStyle(st)}`}>{st}</span>;
            }
            default:
                return "—";
        }
    };

    const renderRentalCell = (key, row) => {
        switch (key) {
            case "rentalId":
                return <span className="font-bold text-gray-600 dark:text-gray-400 text-xs">#{row.Id || row.id || "—"}</span>;
            case "slotNumber":
                return <span className="font-bold text-gray-800 dark:text-gray-100 text-xs">{row.SlotNumber || row.slotNumber || row.slot || "—"}</span>;
            case "rentedFrom":
                return <span className="font-semibold text-gray-800 dark:text-gray-200 text-xs">{row.OwnerUnitName || row.ownerUnitName || row.rentedFrom || "—"}</span>;
            case "rentedTo":
                return <span className="font-semibold text-blue-600 dark:text-blue-400 text-xs">{row.RenterUnitName || row.renterUnitName || row.rentedTo || "—"}</span>;
            case "rentalPeriod": {
                const start = formatDate(row.StartDate || row.startDate || row.rentalPeriodStart);
                const end = formatDate(row.EndDate || row.endDate || row.rentalPeriodEnd);
                return <span className="text-gray-600 dark:text-gray-400 text-xs">{start} to {end}</span>;
            }
            case "monthlyRate": {
                const rate = row.MonthlyRate || row.monthlyRate || row.rentAmount;
                return <span className="text-gray-700 dark:text-gray-300 font-semibold text-xs">{rate ? `₹${rate}` : "—"}</span>;
            }
            case "status": {
                const st = row.Status || row.status || "Active";
                return <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${getStatusBadgeStyle(st)}`}>{st}</span>;
            }
            default:
                return "—";
        }
    };

    const renderBookingCell = (key, row) => {
        switch (key) {
            case "visitorName":
                return <span className="font-semibold text-gray-800 dark:text-gray-100 text-xs">{row.VisitorName || row.visitorName || "—"}</span>;
            case "visitorPhone":
                return <span className="text-gray-600 dark:text-gray-400 text-xs">{row.VisitorPhone || row.visitorPhone || "—"}</span>;
            case "slotNumber":
                return <span className="font-bold text-gray-800 dark:text-gray-100 text-xs">{row.SlotNumber || row.slotNumber || "—"}</span>;
            case "vehicleNumber":
                return <span className="font-mono font-semibold text-gray-700 dark:text-gray-300 text-xs uppercase">{row.VehicleNumber || row.vehicleNumber || "—"}</span>;
            case "startTime":
                return <span className="text-gray-600 dark:text-gray-400 text-xs">{formatDateTime(row.StartDate || row.startDate)}</span>;
            case "endTime":
                return <span className="text-gray-600 dark:text-gray-400 text-xs">{formatDateTime(row.EndDate || row.endDate)}</span>;
            case "amount":
                return <span className="text-gray-700 dark:text-gray-300 font-semibold text-xs">{row.TotalAmount ? `₹${row.TotalAmount}` : "—"}</span>;
            case "paymentStatus": {
                const pStatus = row.PaymentStatus || row.paymentStatus || "Unpaid";
                const isPaid = String(pStatus).toLowerCase() === "paid";
                return (
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${isPaid ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"}`}>
                        {pStatus}
                    </span>
                );
            }
            case "status": {
                const st = row.Status || row.status || "Active";
                return <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${getStatusBadgeStyle(st)}`}>{st}</span>;
            }
            default:
                return "—";
        }
    };

    // Table Content Component for SectionTab
    const renderTableTab = (heading, columns, data, renderCellFunc) => {
        const start = (pageNumber - 1) * pageSize;
        const pageData = data.slice(start, start + pageSize);

        return (
            <div className="bg-white dark:bg-cardbg border border-bordercolor rounded-xl overflow-hidden shadow-sm">
                <CommonTable
                    tableHeading={heading}
                    headers={columns}
                    data={pageData}
                    renderCell={renderCellFunc}
                    pagination={{
                        pageNumber,
                        pageSize,
                        totalPages: Math.ceil(data.length / pageSize) || 1,
                        totalRecords: data.length
                    }}
                    onPageChange={setPageNumber}
                    onLimitChange={(limit) => {
                        setPageSize(limit);
                        setPageNumber(1);
                    }}
                    loading={loading}
                />
            </div>
        );
    };

    // Tab items for SectionTab
    const tabs = [
        {
            key: "slots",
            label: `All Slots (${filteredSlots.length})`,
            component: () => renderTableTab("Parking Bays Directory", slotColumns, filteredSlots, renderSlotCell)
        },
        {
            key: "assignments",
            label: `Permanent Assignments (${filteredAssignments.length})`,
            component: () => renderTableTab("Permanent Vehicle Assignments", assignmentColumns, filteredAssignments, renderAssignmentCell)
        },
        {
            key: "rentals",
            label: `Rental Leases (${filteredRentals.length})`,
            component: () => renderTableTab("Tenant & Unit Rental Leases", rentalColumns, filteredRentals, renderRentalCell)
        },
        {
            key: "bookings",
            label: `Visitor / Pay & Park (${filteredBookings.length})`,
            component: () => renderTableTab("Visitor & Pay-and-Park Sessions", bookingColumns, filteredBookings, renderBookingCell)
        }
    ];

    // Compute Metric Counts
    const totalSlotsCount = summary?.slots?.totalSlots ?? summary?.Slots?.TotalSlots ?? slots.length;
    const availableSlotsCount = summary?.slots?.availableSlots ?? summary?.Slots?.AvailableSlots ?? slots.filter((s) => (s.Status || s.status) === "Available").length;
    const allocatedSlotsCount = summary?.slots?.allocatedSlots ?? summary?.Slots?.AllocatedSlots ?? slots.filter((s) => (s.Status || s.status) === "Allocated").length;

    const activeAssignmentsCount = summary?.assignments?.activeAssignments ?? summary?.Assignments?.ActiveAssignments ?? assignments.filter((a) => (a.Status || a.status) === "Active").length;
    const activeRentalsCount = summary?.rentals?.activeRentals ?? summary?.Rentals?.ActiveRentals ?? rentals.filter((r) => (r.Status || r.status) === "Active" || (r.Status || r.status) === "Rented").length;
    const activeVisitorsCount = summary?.visitors?.activeVisitors ?? summary?.Visitors?.ActiveVisitors ?? bookings.length;

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto gap-5">
            {/* Breadcrumb Navigation */}
            <Breadcrumbs />

            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <PageHeadingSection
                    mainHeading="Parking Reports & Analytics"
                    subHeading={isUnitAdmin ? "Comprehensive parking directory, permanent vehicle assignments, and rental history for your unit" : "Comprehensive parking analytics, slot directory, permanent allocations, rental leases, and visitor parking history"}
                />
                <button
                    onClick={handleExport}
                    className="self-start md:self-auto bg-white dark:bg-cardbg border border-gray-300 dark:border-bordercolor hover:bg-gray-50 dark:hover:bg-actionitembg text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg text-xs font-semibold shadow-sm transition-all flex items-center gap-2"
                >
                    <FontAwesomeIcon icon={faDownload} className="text-blue-600 dark:text-blue-400" />
                    <span>Export to Excel</span>
                </button>
            </div>

            {/* Top KPI Metric Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Total Slots */}
                <div className="bg-white dark:bg-cardbg border border-bordercolor rounded-xl p-4 shadow-sm flex items-center gap-4 transition hover:shadow-md">
                    <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 text-xl">
                        <FontAwesomeIcon icon={faSquareParking} />
                    </div>
                    <div className="flex-1">
                        <p className="text-[11px] font-semibold text-gray-500 dark:text-textsecondary uppercase tracking-wider">Total Bays</p>
                        <h4 className="text-2xl font-black text-gray-800 dark:text-gray-100">{totalSlotsCount}</h4>
                        <div className="flex items-center gap-2 mt-0.5 text-[10px] text-gray-500 dark:text-textsecondary">
                            <span className="text-emerald-600 font-bold">{availableSlotsCount} Free</span>
                            <span>•</span>
                            <span className="text-blue-600 font-bold">{allocatedSlotsCount} Allocated</span>
                        </div>
                    </div>
                </div>

                {/* Permanent Assignments */}
                <div className="bg-white dark:bg-cardbg border border-bordercolor rounded-xl p-4 shadow-sm flex items-center gap-4 transition hover:shadow-md">
                    <div className="w-12 h-12 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 text-xl">
                        <FontAwesomeIcon icon={faUserCheck} />
                    </div>
                    <div className="flex-1">
                        <p className="text-[11px] font-semibold text-gray-500 dark:text-textsecondary uppercase tracking-wider">Active Assignments</p>
                        <h4 className="text-2xl font-black text-gray-800 dark:text-gray-100">{activeAssignmentsCount}</h4>
                        <p className="text-[10px] text-gray-500 dark:text-textsecondary mt-0.5">Permanent unit & tenant vehicles</p>
                    </div>
                </div>

                {/* Active Rentals */}
                <div className="bg-white dark:bg-cardbg border border-bordercolor rounded-xl p-4 shadow-sm flex items-center gap-4 transition hover:shadow-md">
                    <div className="w-12 h-12 rounded-xl bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400 text-xl">
                        <FontAwesomeIcon icon={faKey} />
                    </div>
                    <div className="flex-1">
                        <p className="text-[11px] font-semibold text-gray-500 dark:text-textsecondary uppercase tracking-wider">Active Leases</p>
                        <h4 className="text-2xl font-black text-gray-800 dark:text-gray-100">{activeRentalsCount}</h4>
                        <p className="text-[10px] text-gray-500 dark:text-textsecondary mt-0.5">Unit-to-tenant rental bays</p>
                    </div>
                </div>

                {/* Visitor / Pay & Park */}
                <div className="bg-white dark:bg-cardbg border border-bordercolor rounded-xl p-4 shadow-sm flex items-center gap-4 transition hover:shadow-md">
                    <div className="w-12 h-12 rounded-xl bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-400 text-xl">
                        <FontAwesomeIcon icon={faReceipt} />
                    </div>
                    <div className="flex-1">
                        <p className="text-[11px] font-semibold text-gray-500 dark:text-textsecondary uppercase tracking-wider">Visitor & Short Stay</p>
                        <h4 className="text-2xl font-black text-gray-800 dark:text-gray-100">{activeVisitorsCount}</h4>
                        <p className="text-[10px] text-gray-500 dark:text-textsecondary mt-0.5">Pay-and-park & guest sessions</p>
                    </div>
                </div>
            </div>

            {/* Filter & Search Bar */}
            <div className="bg-white dark:bg-cardbg border border-bordercolor rounded-xl p-4 shadow-sm flex flex-col gap-3">
                <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3">
                    {/* Date Pickers */}
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-gray-600 dark:text-textsecondary flex items-center gap-1.5">
                                <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-400" />
                                From:
                            </span>
                            <input
                                type="date"
                                value={from}
                                onChange={(e) => setFrom(e.target.value)}
                                className="border border-gray-300 dark:border-bordercolor rounded-lg px-2.5 py-1.5 text-xs bg-white dark:bg-actionitembg text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-gray-600 dark:text-textsecondary">To:</span>
                            <input
                                type="date"
                                value={to}
                                onChange={(e) => setTo(e.target.value)}
                                className="border border-gray-300 dark:border-bordercolor rounded-lg px-2.5 py-1.5 text-xs bg-white dark:bg-actionitembg text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                        </div>

                        {/* Quick Presets */}
                        <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-actionitembg p-1 rounded-lg">
                            <button
                                type="button"
                                onClick={() => handlePresetChange("thisMonth")}
                                className="px-2.5 py-1 rounded text-[11px] font-medium text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-cardbg hover:shadow-xs transition"
                            >
                                This Month
                            </button>
                            <button
                                type="button"
                                onClick={() => handlePresetChange("last30Days")}
                                className="px-2.5 py-1 rounded text-[11px] font-medium text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-cardbg hover:shadow-xs transition"
                            >
                                Last 30 Days
                            </button>
                            <button
                                type="button"
                                onClick={() => handlePresetChange("allTime")}
                                className="px-2.5 py-1 rounded text-[11px] font-medium text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-cardbg hover:shadow-xs transition"
                            >
                                All Time
                            </button>
                        </div>
                    </div>

                    {/* Filter & Reset Buttons */}
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => {
                                setPageNumber(1);
                                loadReports(from, to);
                            }}
                            disabled={loading}
                            className="px-4 py-1.5 rounded-lg text-xs font-bold text-white shadow-sm flex items-center gap-2 transition hover:opacity-90 disabled:opacity-50"
                            style={{ backgroundColor: primaryColor() }}
                        >
                            <FontAwesomeIcon icon={loading ? faSpinner : faFilter} className={loading ? "fa-spin" : ""} />
                            <span>{loading ? "Loading..." : "Apply Filter"}</span>
                        </button>
                        <button
                            type="button"
                            onClick={handleReset}
                            className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-bordercolor text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-actionitembg transition flex items-center gap-1.5"
                        >
                            <FontAwesomeIcon icon={faRotateRight} />
                            <span>Reset</span>
                        </button>
                    </div>
                </div>

                {/* Search Bar */}
                <div className="relative w-full">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                        <FontAwesomeIcon icon={faSearch} className="text-xs" />
                    </div>
                    <input
                        type="text"
                        placeholder="Search by slot number, unit, vehicle number, user, or status..."
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setPageNumber(1);
                        }}
                        className="w-full pl-9 pr-4 py-2 border border-gray-200 dark:border-bordercolor rounded-lg text-xs bg-gray-50/50 dark:bg-actionitembg text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    {searchQuery && (
                        <button
                            onClick={() => setSearchQuery("")}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-xs text-gray-400 hover:text-gray-600"
                        >
                            ✕
                        </button>
                    )}
                </div>
            </div>

            {/* Tabbed Reports Section */}
            <div className="flex flex-col flex-1">
                <SectionTab
                    tabs={tabs}
                    defaultTab="slots"
                    activeTab={activeTab}
                    onTabChange={(tabKey) => {
                        setActiveTab(tabKey);
                        setPageNumber(1);
                    }}
                />
            </div>
        </div>
    );
};

export default ParkingReports;
